import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import '../../components/api-reference.css';
import { PostCallPromptLibrary } from '../../components/PostCallPromptLibrary';
import {
  Settings,
  Key,
  Plus,
  Trash2,
  Copy,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Phone,
  Globe,
  Camera,
  Code,
  Link2,
  Eye,
  EyeOff,
  Info,
  Zap,
  Calendar,
  RefreshCw,
  CreditCard,
  ChevronDown,
  Plug,
  FileCode,
  DollarSign,
  Package,
  Clock,
  TrendingUp,
  Receipt,
  MessageCircle,
  MessageSquare,
  Users,
  Mail,
  Shield,
  UserPlus
} from 'lucide-react';

// Wistia video IDs for the per-tab LeadConnector Setup tutorials.
// Add a new entry here to swap the 5-step guide for a video on that tab.
const SETUP_VIDEOS = {
};

const SubAccountSettingsContent = () => {
  const { workspaceId, subAccountId } = useParams();
  const { api } = useAuth();
  const { subAccount, refreshData } = useSubAccount();
  const { isDarkMode } = useTheme();
  
  // Category navigation — default from URL param ?tab=billing
  const urlTab = new URLSearchParams(window.location.search).get('tab');
  const [activeCategory, setActiveCategory] = useState(urlTab || 'connections');
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [revokingKey, setRevokingKey] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  
  // API Reference state
  const [activeApiTab, setActiveApiTab] = useState('outbound');
  
  // LeadConnector/CRM state
  const [ghlStatus, setGhlStatus] = useState(null);
  const [loadingGhl, setLoadingGhl] = useState(true);
  const [connectingGhl, setConnectingGhl] = useState(false);
  const [disconnectingGhl, setDisconnectingGhl] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [calendarDropdownOpen, setCalendarDropdownOpen] = useState(false);
  const [retryingGhl, setRetryingGhl] = useState(false);

  // Members (Phase 2) state
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberInviteOpen, setMemberInviteOpen] = useState(false);
  const [memberInvite, setMemberInvite] = useState({ email: '', name: '', role: 'client_admin', password: '' });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [lastInviteCreds, setLastInviteCreds] = useState(null); // { email, temp_password, login_url }

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/members`);
      setMembers(res.data?.members || []);
    } catch { /* ignore */ }
    finally { setLoadingMembers(false); }
  };

  const generateInvitePassword = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setMemberInvite((m) => ({ ...m, password: out }));
  };

  const sendMemberInvite = async (e) => {
    e?.preventDefault?.();
    if (!memberInvite.email) return;
    setSendingInvite(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/members/invite`, memberInvite);
      setLastInviteCreds({
        email: res.data?.email || memberInvite.email,
        temp_password: res.data?.temp_password,
        login_url: res.data?.login_url,
      });
      setMemberInvite({ email: '', name: '', role: 'client_admin', password: '' });
      toast.success('Member created. Share the credentials below.');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create member');
    } finally { setSendingInvite(false); }
  };

  const updateMemberRole = async (memberId, role) => {
    try {
      await api.patch(`/api/sub-accounts/${subAccountId}/members/${memberId}`, { role });
      fetchMembers();
    } catch { toast.error('Failed to update role'); }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/members/${memberId}`);
      toast.success('Member removed');
      fetchMembers();
    } catch { toast.error('Failed to remove member'); }
  };

  useEffect(() => {
    if (activeCategory === 'members' && subAccountId) fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, subAccountId]);


  // Webhook base URL — derived from current browser origin so each environment
  // (production voxinity.ai, preview, white-label custom domain) shows its OWN
  // URL automatically. iter139: previously overridden by a backend
  // `/api/config/webhook-base-url` call which read WEBHOOK_BASE_URL from the
  // server's env. When that env was misconfigured on a deploy, every customer
  // saw the wrong URL in their API & Webhooks docs (production showing preview
  // URLs, etc.). Trusting `window.location.origin` removes that single point
  // of failure — the URL the user is literally looking at always matches the
  // domain in their address bar.
  const [webhookBaseUrl, setWebhookBaseUrl] = useState(window.location.origin);

  // Billing state
  const [billingData, setBillingData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  // Total spend banner — server-computed across ALL filtered records.
  const [txUsageTotal, setTxUsageTotal] = useState(0);
  const [txUsageCount, setTxUsageCount] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [txStartDate, setTxStartDate] = useState('');
  const [txEndDate, setTxEndDate] = useState('');
  const [txPage, setTxPage] = useState(0);
  const [txOpen, setTxOpen] = useState(false);
  const [liveUsage, setLiveUsage] = useState(null);
  const TX_PAGE_SIZE = 15;

  // Credit purchase state
  const [selectedPackage, setSelectedPackage] = useState('small');
  const [purchasing, setPurchasing] = useState(false);
  const [autoTopUp, setAutoTopUp] = useState({ enabled: false, threshold: 100, amount: 1000, package_id: 'small' });
  const [savingAutoTopUp, setSavingAutoTopUp] = useState(false);

  // ── Wallet (redesigned billing tab) ────────────────────────────────────
  // Backend `/api/sub-accounts/{id}/wallet` is the single source of truth
  // for: balance, default-card metadata, and auto-pay config. We never
  // touch Stripe directly from the frontend — Stripe still handles all
  // payment processing through the existing Checkout / setup-intent flow.
  const [wallet, setWallet] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  // Modal state — dollar amounts (NOT credits)
  const [topUpAmount, setTopUpAmount] = useState(50);  // selected/typed top-up dollar amount
  const [topUpCustom, setTopUpCustom] = useState('');  // custom $-input value
  // Auto-pay form inside the modal
  const [autoPayForm, setAutoPayForm] = useState({
    enabled: false,
    threshold_amount: 5,
    topup_amount: 50,
  });
  const [savingAutoPay, setSavingAutoPay] = useState(false);

  const TOP_UP_BUNDLES = [
    { amt: 10, desc: 'Get started' },
    { amt: 25, desc: 'Light use' },
    { amt: 50, desc: 'Recommended' },
    { amt: 100, desc: 'Most popular', popular: true },
    { amt: 250, desc: 'Power user' },
    { amt: 500, desc: 'Agency scale' },
  ];

  const CREDIT_PACKAGES = {
    small: { credits: 1000, price: 10, label: '1,000' },
    medium: { credits: 5000, price: 45, label: '5,000' },
    large: { credits: 10000, price: 80, label: '10,000' },
    xl: { credits: 25000, price: 175, label: '25,000' },
    xxl: { credits: 50000, price: 300, label: '50,000' },
  };

  // Theme classes
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';
  const cardClass = `${cardBg} border ${cardBorder}`;
  const inputClass = `${inputBg} ${textPrimary} ${cardBorder}`;

  useEffect(() => {
    fetchApiKeys();
    fetchGhlStatus();
    fetchBillingData();
    // iter139: do NOT override webhookBaseUrl from backend env — the browser
    // origin is always the right answer (production users see voxinity.ai,
    // preview users see preview, white-label users see their custom domain).
    // The previous backend override caused production deployments to leak
    // preview URLs to live users when WEBHOOK_BASE_URL was misconfigured.
    
    const urlParams = new URLSearchParams(window.location.search);
    const ghlParam = urlParams.get('ghl');
    if (ghlParam === 'connected') {
      toast.success('CRM connected successfully!');
      fetchGhlStatus();
      refreshData();
      window.history.replaceState({}, document.title, window.location.pathname + '?tab=billing');
    }

    // Handle Stripe credit purchase return
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      verifyCreditPurchase(sessionId);
    }
  }, [subAccountId]);

  const fetchGhlStatus = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/status`);
      setGhlStatus(response.data);
      if (response.data.connected) {
        fetchCalendars();
      }
    } catch (error) {
      console.error('Failed to fetch CRM status:', error);
    } finally {
      setLoadingGhl(false);
    }
  };

  const fetchCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/calendars`);
      setCalendars(response.data.calendars || []);
      setSelectedCalendarId(response.data.selected_calendar_id || null);
    } catch (error) {
      console.error('Failed to fetch calendars:', error);
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleSelectCalendar = async (calendarId, calendarName) => {
    setSavingCalendar(true);
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/ghl/calendar`, {
        calendar_id: calendarId,
        calendar_name: calendarName
      });
      setSelectedCalendarId(calendarId);
      setCalendarDropdownOpen(false);
      toast.success(`Calendar "${calendarName}" selected`);
    } catch (error) {
      toast.error('Failed to select calendar');
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleConnectGhl = async () => {
    setConnectingGhl(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/oauth/authorize`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('LeadConnector OAuth error:', error);
      const detail = error.response?.data?.detail || '';
      if (detail.includes('redirect_uri')) {
        toast.error('Redirect URI mismatch. Please update your LeadConnector app\'s redirect URI.');
      } else {
        toast.error(detail || 'Failed to initialize connection.');
      }
      setConnectingGhl(false);
    }
  };

  const handleDisconnectGhl = async () => {
    if (!window.confirm('Disconnect CRM? All data for this location will be wiped — calendar selections, custom field mappings, and CRM conversations. Reconnecting (same or new location) starts fresh.')) return;
    setDisconnectingGhl(true);
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/ghl/disconnect`);
      toast.success('CRM disconnected');
      setGhlStatus({ connected: false });
      refreshData();
    } catch (error) {
      toast.error('Failed to disconnect CRM');
    } finally {
      setDisconnectingGhl(false);
    }
  };

  const handleRetryGhl = async () => {
    setRetryingGhl(true);
    try {
      const response = await api.post(`/api/sub-accounts/${subAccountId}/ghl/retry-connection`);
      if (response.data.success) {
        toast.success('CRM connection restored!');
        fetchGhlStatus();
        refreshData();
      } else if (response.data.needs_reconnect) {
        toast.error('Token mismatch — click Reconnect to re-authorize.', { duration: 6000 });
      } else {
        toast.error(response.data.message || 'Retry failed — try Reconnect.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingGhl(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/api-keys`);
      setApiKeys(response.data || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoadingKeys(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/credits`);
      setBillingData(response.data);
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    }
    setLoadingBilling(false);
    fetchTransactionHistory();
    fetchWallet();
  };

  // ── Wallet ──────────────────────────────────────────────────────────────
  // Source of truth for balance, default-card metadata, and auto-pay
  // config. Stripe stays in charge of all actual payment processing —
  // this is purely a read endpoint plus a save endpoint for preferences.
  const fetchWallet = async () => {
    setLoadingWallet(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/wallet`);
      setWallet(res.data);
      // Hydrate the auto-pay form so the modal opens with current values.
      const atp = res.data?.auto_topup || {};
      setAutoPayForm({
        enabled: !!atp.enabled,
        threshold_amount: Number(atp.threshold_amount ?? 5),
        topup_amount: Number(atp.topup_amount ?? 50),
      });
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
    } finally {
      setLoadingWallet(false);
    }
  };

  // Open the Add Balance modal. Pre-fills with the recommended $50 bundle
  // so a single click takes the user to Stripe Checkout.
  const handleOpenAddBalance = () => {
    setTopUpAmount(50);
    setTopUpCustom('');
    setShowAddBalanceModal(true);
  };

  // Kick off Stripe Checkout for an arbitrary dollar amount. The /purchase
  // -credits endpoint accepts package_id="custom" + custom_amount so we
  // never have to map dollar values to fixed package IDs from the UI.
  const handleTopUpDollar = async (amount) => {
    const amt = Number(amount);
    if (!amt || amt < 10) {
      toast.error('Minimum top-up is $10');
      return;
    }
    if (amt > 9999) {
      toast.error('Maximum top-up is $9,999');
      return;
    }
    setPurchasing(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/purchase-credits`, {
        package_id: 'custom',
        custom_amount: amt,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        // Stripe handles the actual payment — we just hand off the user.
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  // Persist the auto-pay preferences. NO Stripe API call here — that
  // happens later when the off-session top-up loop charges the saved
  // payment method. We only store preferences.
  const handleSaveAutoPay = async () => {
    setSavingAutoPay(true);
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/self-auto-topup`, {
        enabled: autoPayForm.enabled,
        threshold_amount: autoPayForm.threshold_amount,
        topup_amount: autoPayForm.topup_amount,
        // Legacy compat fields — backend keeps them populated too.
        threshold: Math.round(autoPayForm.threshold_amount * 100),
        amount: Math.round(autoPayForm.topup_amount * 100),
        package_id: 'small',
      });
      toast.success(autoPayForm.enabled ? 'Auto-pay enabled' : 'Auto-pay disabled');
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save auto-pay settings');
    } finally {
      setSavingAutoPay(false);
    }
  };

  const fetchTransactionHistory = async (page = 0) => {
    setTxLoading(true);
    try {
      let url = `/api/sub-accounts/${subAccountId}/credits/transactions?limit=${TX_PAGE_SIZE}&skip=${page * TX_PAGE_SIZE}`;
      if (txStartDate) url += `&start_date=${txStartDate}`;
      if (txEndDate) url += `&end_date=${txEndDate}`;
      const response = await api.get(url);
      setTransactions(response.data?.transactions || []);
      setTxTotal(response.data?.total || 0);
      setTxUsageTotal(Number(response.data?.total_usage_amount || 0));
      setTxUsageCount(Number(response.data?.total_usage_count || 0));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setTransactions([]);
    }
    setTxLoading(false);
  };

  const handleTxApply = () => { setTxPage(0); fetchTransactionHistory(0); };
  const handleTxClear = () => { setTxStartDate(''); setTxEndDate(''); setTxPage(0); setTimeout(() => fetchTransactionHistory(0), 50); };
  const handleTxPageChange = (p) => { setTxPage(p); fetchTransactionHistory(p); };

  // Credit purchase via Stripe Checkout
  const handlePurchaseCredits = async () => {
    setPurchasing(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/purchase-credits`, {
        package_id: selectedPackage,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  const verifyCreditPurchase = async (sessionId) => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/purchase-credits/status/${sessionId}`);
      if (res.data.status === 'completed') {
        toast.success(`${res.data.credits_added.toLocaleString()} credits added!`);
        fetchBillingData();
      }
    } catch (error) {
      console.error('Failed to verify purchase:', error);
    }
    // Clean session_id from URL
    const url = new URL(window.location);
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.toString());
  };

  const handleSaveAutoTopUp = async () => {
    setSavingAutoTopUp(true);
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/self-auto-topup`, autoTopUp);
      toast.success(autoTopUp.enabled ? 'Auto top-up enabled' : 'Auto top-up disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingAutoTopUp(false);
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      purchase: 'bg-green-400/10 text-green-400',
      payment: 'bg-emerald-400/10 text-emerald-400',
      transfer: 'bg-violet-400/10 text-violet-400',
      usage: 'bg-red-400/10 text-red-400',
      voice: 'bg-blue-400/10 text-blue-400',
      voice_usage: 'bg-blue-400/10 text-blue-400',
      voice_ai: 'bg-teal-400/10 text-teal-400',
      voice_ai_usage: 'bg-teal-400/10 text-teal-400',
      voice_pending: 'bg-amber-400/10 text-amber-400',
      chat: 'bg-cyan-400/10 text-cyan-400',
      chat_usage: 'bg-cyan-400/10 text-cyan-400',
      bonus: 'bg-amber-400/10 text-amber-400',
    };
    const labels = { purchase: 'Purchase', payment: 'Payment', transfer: 'Transfer', usage: 'Usage', voice: 'Voice', voice_usage: 'Voice', voice_ai: 'Voice AI', voice_ai_usage: 'Voice AI', voice_pending: 'Voice pending', chat: 'Chat', chat_usage: 'Chat', bonus: 'Bonus' };
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full ${styles[type] || 'bg-gray-400/10 text-gray-400'}`}>
        {labels[type] || type || 'other'}
      </span>
    );
  };

  const txTotalPages = Math.ceil(txTotal / TX_PAGE_SIZE);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { toast.error('Enter a name for the key'); return; }
    setCreatingKey(true);
    try {
      const response = await api.post(`/api/sub-accounts/${subAccountId}/api-keys`, { name: newKeyName });
      setNewlyCreatedKey(response.data);
      setNewKeyName('');
      fetchApiKeys();
      toast.success('API key created');
    } catch (error) {
      toast.error('Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (confirmDelete !== keyId) {
      setConfirmDelete(keyId);
      return;
    }
    setRevokingKey(keyId);
    setConfirmDelete(null);
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/api-keys/${keyId}`);
      toast.success('API key revoked successfully');
      fetchApiKeys();
    } catch (error) {
      console.error('Revoke key error:', error?.response?.status, error?.response?.data);
      toast.error(error?.response?.data?.detail || 'Failed to revoke key');
    } finally {
      setRevokingKey(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      // Fallback for browsers that block clipboard API
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Copied to clipboard');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const apiBaseUrl = webhookBaseUrl;

  const categories = [
    { id: 'connections', icon: Plug, label: 'Connections' },
    { id: 'members', icon: Users, label: 'Members' },
    { id: 'api', icon: Key, label: 'API & Webhooks' },
    { id: 'billing', icon: CreditCard, label: 'Billing' },
    { id: 'byok', icon: Key, label: 'BYOK' },
  ];

  const selectedCalendar = calendars.find(c => c.id === selectedCalendarId);

  // ─── API Reference Data ───
  const apiDocs = {
    outbound: {
      title: 'Outbound Call', method: 'POST', endpoint: '/api/webhooks/outbound/trigger',
      description: 'Trigger an outbound AI voice call. The AI agent will use ElevenLabs ConvAI with all configured LeadConnector functions.',
      fields: [
        { name: 'agent_id', type: 'string', required: true, description: 'Your agent UUID (found in Agent Editor URL)' },
        { name: 'to_number', type: 'string', required: true, description: 'Phone number to call (E.164 format, e.g. +14155551234)' },
        { name: 'context', type: 'object', required: false, description: 'Lead context passed to AI (first_name, company, notes, etc.)' },
        { name: 'api_key', type: 'string', required: true, description: 'Your API key. Can also be sent via X-API-Key header.' }
      ],
      example: {
        request: `curl -X POST ${apiBaseUrl}/api/webhooks/outbound/trigger \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "sk_live_uXf...",
    "agent_id": "your-agent-uuid",
    "to_number": "+14155551234"
  }'`,
        response: `{ "call_id": "call_...", "status": "queued", "agent_id": "...", "to_number": "+14155551234" }`
      }
    },
    scraper: {
      title: 'Web Scraper', method: 'POST', endpoint: '/api/webhooks/tools/scrape-website',
      description: 'Scrape a website and extract business name, description, services, and contact info for LeadConnector custom fields.',
      fields: [
        { name: 'url', type: 'string', required: true, description: 'Website URL to scrape' },
        { name: 'api_key', type: 'string', required: true, description: 'Your API key. Can also be sent via X-API-Key header.' }
      ],
      example: {
        request: `curl -X POST ${apiBaseUrl}/api/webhooks/tools/scrape-website \\
  -H "Content-Type: application/json" \\
  -d '{ "api_key": "sk_live_uXf...", "url": "https://example.com" }'`,
        response: `{
  "business_name": "Acme Inc",
  "business_description": "Full-service marketing agency specializing in digital strategy and brand development.",
  "industry": "Marketing",
  "services": ["SEO", "Social Media", "Web Design"],
  "contact": { "phone": "+1...", "email": "..." }
}`
      }
    },
    screenshot: {
      title: 'Screenshot', method: 'POST', endpoint: '/api/webhooks/tools/screenshot',
      description: 'Capture a screenshot of any website and get a hosted image URL.',
      fields: [
        { name: 'url', type: 'string', required: true, description: 'Website URL to screenshot' },
        { name: 'full_page', type: 'boolean', required: false, description: 'Capture full scrollable page' },
        { name: 'api_key', type: 'string', required: true, description: 'Your API key. Can also be sent via X-API-Key header.' }
      ],
      example: {
        request: `curl -X POST ${apiBaseUrl}/api/webhooks/tools/screenshot \\
  -H "Content-Type: application/json" \\
  -d '{ "api_key": "sk_live_uXf...", "url": "https://example.com" }'`,
        response: `{ "screenshot_url": "https://storage...", "width": 1280, "height": 800 }`
      }
    },
    followup: {
      title: 'Follow-Up Chat', method: 'POST', endpoint: '/api/webhooks/follow-up-chat',
      description: 'Generate and send a personalized AI follow-up message to re-engage a lead via LeadConnector chat. Uses past conversation history and a chosen personality trait.',
      fields: [
        { name: 'agent_id', type: 'string', required: true, description: 'Your chat agent UUID (found in Agent Editor URL)' },
        { name: 'contact_id', type: 'string', required: true, description: 'LeadConnector contact ID — identifies who to message' },
        { name: 'trait', type: 'string', required: false, description: 'Personality tone: professional, humorous, friendly, urgent, casual (default: professional)' },
        { name: 'api_key', type: 'string', required: true, description: 'Your API key. Can also be sent via X-API-Key header.' }
      ],
      example: {
        request: `curl -X POST ${apiBaseUrl}/api/webhooks/follow-up-chat \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "sk_live_uXf...",
    "agent_id": "your-agent-uuid",
    "contact_id": "ghl-contact-id",
    "trait": "friendly"
  }'`,
        response: `{
  "success": true,
  "status": "queued",
  "job_id": "fuc_a1b2c3d4e5f6g7h8",
  "message": "Follow-up message queued for delivery."
}`
      }
    },
    postcallsms: {
      title: 'Post-Call SMS', method: 'POST', endpoint: '/api/webhooks/post-call-sms/trigger',
      description: 'After an AI call ends, classify the outcome (booked / voicemail / no-answer / not-interested / etc.) with an LLM and send ONE personalized SMS via LeadConnector Conversations. Drop into your LeadConnector "Call Ended" workflow — fields below mirror what LeadConnector exposes.',
      fields: [
        { name: 'call_id', type: 'string', required: true, description: 'Map call_id from the Voxinity post-call webhook event. Do not use a Twilio SID or assume a contact last-call field is populated. If your workflow stores that field, update it before this action.' },
        { name: 'prompt_id', type: 'string', required: false, description: 'Copy the ID of a saved prompt below. Send as Custom Data to choose that workflow’s prompt. Omit for the existing default. Unknown IDs fail without sending.' },
        { name: 'api_key', type: 'string', required: true, description: 'Your API key. Can also be sent via X-API-Key header or as ?api_key= query param.' },
      ],
      example: {
        request: `curl -X POST ${apiBaseUrl}/api/webhooks/post-call-sms/trigger \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "sk_live_uXf...",
    "call_id": "<call_id from the Voxinity post-call webhook>",
    "customData": { "prompt_id": "<saved prompt ID>" }
  }'`,
        response: `{
  "status": "success",        // success | failed | skipped
  "reason": "accepted_by_ghl",
  "data": {
    "call_id": "cb_call_...",
    "outcome": "voicemail",
    "sms_text": "Hey {{contact.first_name}}, just tried calling...",
    "message_id": "ghl_msg_...",
    "sent_at": "2026-02-12T..."
  }
}`
      }
    }
  };
  const activeDoc = apiDocs[activeApiTab];

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="settings-page">
      {isDarkMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-violet-400/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-teal-400/5 rounded-full blur-[80px]" />
        </div>
      )}

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className={`text-2xl font-display font-semibold ${textPrimary}`}>Settings</h1>
          <p className={`text-sm ${textSecondary}`}>Manage integrations, API access, and billing</p>
        </motion.div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Left Sidebar - Categories */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="w-full md:w-52 shrink-0">
            <nav className="grid grid-cols-2 gap-1 md:block md:space-y-1 md:sticky md:top-6">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`settings-cat-${cat.id}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? `${isDarkMode ? 'bg-white/10' : 'bg-gray-100'} ${textPrimary}`
                      : `${textSecondary} hover:${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`
                  }`}
                >
                  <cat.icon size={18} className={activeCategory === cat.id ? 'text-teal-400' : ''} />
                  {cat.label}
                </button>
              ))}
            </nav>
          </motion.div>

          {/* Right Content */}
          <div className="flex-1 min-w-0">
            {/* ═══════════════ CONNECTIONS ═══════════════ */}
            {activeCategory === 'connections' && (
              <motion.div key="connections" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                
                {/* CRM Connection - Compact Card */}
                <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder}`}>
                  <div className={`p-4 border-b ${cardBorder} flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-400/20 to-red-500/20 flex items-center justify-center">
                        <Zap className="text-orange-400" size={18} />
                      </div>
                      <div>
                        <h2 className={`text-sm font-semibold ${textPrimary}`}>LeadConnector CRM</h2>
                        <p className={`text-xs ${textSecondary}`}>Contact sync, workflows, and calendar booking</p>
                      </div>
                    </div>
                    {ghlStatus?.connected && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-400/10 text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        Connected
                      </span>
                    )}
                  </div>
                  
                  <div className="p-4">
                    {loadingGhl ? (
                      <div className="flex justify-center py-6"><Loader2 className="animate-spin text-teal-400" size={24} /></div>
                    ) : ghlStatus?.needs_reconnect ? (
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <p className={`text-sm font-medium text-red-400`}>Session Expired</p>
                          <p className={`text-xs ${textSecondary}`}>
                            {ghlStatus.error?.includes('Invalid client credentials')
                              ? 'App credentials changed — re-authorize to reconnect'
                              : ghlStatus.location_name ? `Previously: ${ghlStatus.location_name}` : 'Re-authorize to resume sync'}
                          </p>
                        </div>
                        <button onClick={handleConnectGhl} disabled={connectingGhl} data-testid="reconnect-ghl-btn"
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-orange-400 to-red-500 text-white text-sm font-medium disabled:opacity-50">
                          {connectingGhl ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          Reconnect
                        </button>
                        <button onClick={handleDisconnectGhl} disabled={disconnectingGhl} data-testid="disconnect-ghl-expired-btn"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-400 text-xs font-medium bg-red-400/10 hover:bg-red-400/20 transition-colors disabled:opacity-50">
                          {disconnectingGhl ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                          Disconnect Instead
                        </button>
                      </div>
                    ) : ghlStatus?.connected ? (
                      <div className="space-y-3">
                        {/* Connection Details - Inline */}
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-0.5`}>Location</p>
                            <p className={`text-sm font-medium ${textPrimary}`}>{ghlStatus.location_name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-0.5`}>Location ID</p>
                            <p className={`text-xs font-mono ${textSecondary}`}>{ghlStatus.location_id || 'N/A'}</p>
                          </div>
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-0.5`}>Connected</p>
                            <p className={`text-sm ${textPrimary}`}>
                              {ghlStatus.connected_at ? new Date(ghlStatus.connected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-end justify-end">
                            <button onClick={handleDisconnectGhl} disabled={disconnectingGhl} data-testid="disconnect-ghl-btn"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-400 text-xs font-medium bg-red-400/10 hover:bg-red-400/20 transition-colors disabled:opacity-50">
                              {disconnectingGhl ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                              Disconnect
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <p className={`text-sm ${textSecondary}`}>Link your LeadConnector account for contact sync, calendar booking, and workflow triggers.</p>
                        </div>
                        <button onClick={handleConnectGhl} disabled={connectingGhl} data-testid="connect-ghl-btn"
                          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-orange-400/25 transition-all disabled:opacity-50 shrink-0">
                          {connectingGhl ? <><Loader2 size={16} className="animate-spin" /> Connecting...</> : <><ExternalLink size={16} /> Connect</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ═══════════════ API & WEBHOOKS ═══════════════ */}

            {/* ═══════════════ MEMBERS ═══════════════ */}
            {activeCategory === 'members' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="sa-members-panel">
                <div className={`${cardClass} rounded-2xl overflow-hidden`}>
                  <div className={`p-6 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                          <Users className="text-teal-400" size={20} />
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold ${textPrimary}`}>Members</h3>
                          <p className={`text-sm ${textSecondary}`}>
                            Grant clients access to this sub-account only. They never see the agency workspace.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setMemberInviteOpen(!memberInviteOpen); setLastInviteCreds(null); }}
                        className="px-3.5 py-2 rounded-lg bg-teal-400/15 hover:bg-teal-400/25 text-teal-300 text-sm font-semibold inline-flex items-center gap-1.5 border border-teal-400/30"
                        data-testid="sa-members-invite-toggle"
                      >
                        <UserPlus size={14} /> Add member
                      </button>
                    </div>
                  </div>

                  {memberInviteOpen && (
                    <form
                      onSubmit={sendMemberInvite}
                      className={`p-5 border-b ${isDarkMode ? 'border-white/[0.05] bg-white/[0.015]' : 'border-gray-200 bg-gray-50/50'} space-y-3`}
                      data-testid="sa-members-invite-form"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input
                          required type="email" placeholder="client@example.com"
                          value={memberInvite.email}
                          onChange={(e) => setMemberInvite({ ...memberInvite, email: e.target.value })}
                          className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                          data-testid="sa-invite-email"
                        />
                        <input
                          type="text" placeholder="Name (optional)"
                          value={memberInvite.name}
                          onChange={(e) => setMemberInvite({ ...memberInvite, name: e.target.value })}
                          className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                          data-testid="sa-invite-name"
                        />
                        <select
                          value={memberInvite.role}
                          onChange={(e) => setMemberInvite({ ...memberInvite, role: e.target.value })}
                          className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                          data-testid="sa-invite-role"
                        >
                          <option value="client_admin">Client Admin · full access + invite others</option>
                          <option value="client_viewer">Client Viewer · read-only (can add billing CC)</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="text" placeholder="Temporary password (or click Generate)"
                          value={memberInvite.password}
                          onChange={(e) => setMemberInvite({ ...memberInvite, password: e.target.value })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                          data-testid="sa-invite-password"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={generateInvitePassword}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold border ${isDarkMode ? 'bg-white/5 hover:bg-white/10 border-white/10 text-white' : 'bg-white hover:bg-gray-100 border-gray-200 text-gray-700'}`}
                          data-testid="sa-invite-generate-password"
                        >
                          Generate
                        </button>
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <p className={`text-[11px] ${textSecondary}`}>
                          <Shield size={10} className="inline mr-1" />
                          Leave password empty to auto-generate. The member changes it on first sign-in.
                        </p>
                        <button
                          type="submit" disabled={sendingInvite}
                          className="px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold disabled:opacity-60"
                          data-testid="sa-invite-submit"
                        >
                          {sendingInvite ? 'Creating…' : 'Create member'}
                        </button>
                      </div>

                      {lastInviteCreds && (
                        <div className={`mt-2 p-4 rounded-lg border ${isDarkMode ? 'bg-teal-400/[0.05] border-teal-400/25' : 'bg-teal-50 border-teal-200'} space-y-2`} data-testid="sa-invite-credentials-banner">
                          <p className={`text-xs font-semibold ${textPrimary}`}>Share these credentials with your client · one-time display</p>
                          <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-center text-xs">
                            <span className={textSecondary}>Email</span>
                            <code className={`font-mono ${textPrimary} truncate`}>{lastInviteCreds.email}</code>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(lastInviteCreds.email); toast.success('Email copied'); }} className="p-1.5 rounded hover:bg-white/10" data-testid="sa-invite-copy-email"><Copy size={12} /></button>

                            <span className={textSecondary}>Password</span>
                            <code className={`font-mono ${textPrimary} truncate`}>{lastInviteCreds.temp_password}</code>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(lastInviteCreds.temp_password); toast.success('Password copied'); }} className="p-1.5 rounded hover:bg-white/10" data-testid="sa-invite-copy-password"><Copy size={12} /></button>

                            <span className={textSecondary}>Login URL</span>
                            <code className={`font-mono text-teal-400 truncate`}>{lastInviteCreds.login_url}</code>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(lastInviteCreds.login_url); toast.success('Login URL copied'); }} className="p-1.5 rounded hover:bg-white/10" data-testid="sa-invite-copy-url"><Copy size={12} /></button>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const text = `Hi${lastInviteCreds.email ? ' ' + lastInviteCreds.email : ''},\n\nYour portal is ready. Sign in here:\n${lastInviteCreds.login_url}\n\nEmail: ${lastInviteCreds.email}\nTemporary password: ${lastInviteCreds.temp_password}\n\nYou'll be asked to set a new password on first sign-in.`;
                              navigator.clipboard.writeText(text);
                              toast.success('Ready-to-send message copied');
                            }}
                            className="text-[11px] text-teal-400 hover:text-teal-300 underline"
                            data-testid="sa-invite-copy-message"
                          >
                            Copy a ready-to-send message
                          </button>
                        </div>
                      )}
                    </form>
                  )}

                  <div className="p-5">
                    {loadingMembers ? (
                      <div className={`text-sm ${textSecondary}`}>Loading…</div>
                    ) : members.length === 0 ? (
                      <div className="text-center py-10">
                        <Users className={`mx-auto mb-3 ${textSecondary}`} size={32} />
                        <p className={`text-sm ${textSecondary}`}>No members yet. Invite your first client above.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {members.map((m) => (
                          <div
                            key={m.member_id}
                            className={`flex items-center gap-3 p-3 rounded-xl border ${isDarkMode ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-white'}`}
                            data-testid={`sa-member-${m.member_id}`}
                          >
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                              isDarkMode ? 'bg-teal-400/15 text-teal-300 border border-teal-400/25' : 'bg-teal-50 text-teal-700 border border-teal-200'
                            }`}>
                              {(m.name || m.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${textPrimary} truncate`}>{m.name || m.email}</div>
                              <div className={`text-xs ${textSecondary} truncate flex items-center gap-1.5`}>
                                <Mail size={10} /> {m.email}
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                  m.status === 'active'
                                    ? 'bg-emerald-400/15 text-emerald-300'
                                    : 'bg-amber-400/15 text-amber-300'
                                }`}>{m.status}</span>
                              </div>
                            </div>
                            <select
                              value={m.role}
                              onChange={(e) => updateMemberRole(m.member_id, e.target.value)}
                              className={`text-xs px-2 py-1.5 rounded-md ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                              data-testid={`sa-member-role-${m.member_id}`}
                            >
                              <option value="client_admin">Client Admin</option>
                              <option value="client_viewer">Client Viewer</option>
                            </select>
                            <button
                              onClick={() => removeMember(m.member_id)}
                              className="p-1.5 rounded-md text-rose-400/80 hover:text-rose-400 hover:bg-rose-400/10"
                              data-testid={`sa-member-remove-${m.member_id}`}
                              title="Remove member"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}


            {activeCategory === 'api' && (
              <motion.div key="api" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="subaccount-api-clean space-y-4" data-theme={isDarkMode ? 'dark' : 'light'} data-testid="subaccount-api-section">
                
                {/* API Keys */}
                <section className="api-panel api-keys-panel" data-testid="api-keys-panel">
                  <div className="api-panel-heading flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400/20 to-orange-500/20 flex items-center justify-center">
                        <Key className="text-amber-400" size={18} />
                      </div>
                      <div>
                        <h2 data-testid="api-keys-heading">API Keys</h2>
                        <p className={`text-xs ${textSecondary}`}>Authenticate webhook integrations</p>
                      </div>
                    </div>
                    <button onClick={() => setShowCreateModal(true)} data-testid="create-api-key-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-400 text-[#08090d] text-xs font-medium hover:bg-teal-300 transition-colors">
                      <Plus size={14} /> Create Key
                    </button>
                  </div>

                  <div className="api-panel-body">
                    {loadingKeys ? (
                      <div className="flex justify-center py-6"><Loader2 className="animate-spin text-teal-400" size={24} /></div>
                    ) : apiKeys.length > 0 ? (
                      <div className="space-y-2">
                        {apiKeys.map((key) => (
                          <div key={key.key_id} data-testid={`api-key-${key.key_id}`}
                            className="api-key-row flex items-center justify-between gap-3 p-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <Key className="text-amber-400 shrink-0" size={16} />
                              <div className="min-w-0">
                                <h4 className={`text-sm font-medium ${textPrimary}`} data-testid={`api-key-name-${key.key_id}`}>{key.name}</h4>
                                <p className={`text-[10px] font-mono ${textSecondary} truncate`}>
                                  {visibleKeys[key.key_id]
                                    ? (key.raw_key || key.key_preview)
                                    : (key.raw_key || key.key_preview || '').slice(0, 10) + '••••••••••••••••••' + (key.raw_key || key.key_preview || '').slice(-4)
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <p className={`text-[10px] ${textSecondary} hidden sm:block`}>Last used: {formatDate(key.last_used_at)}</p>
                              <button onClick={() => setVisibleKeys(prev => ({ ...prev, [key.key_id]: !prev[key.key_id] }))}
                                data-testid={`toggle-key-${key.key_id}`}
                                className={`p-1.5 rounded-lg text-xs ${textSecondary} hover:text-teal-400 hover:bg-teal-400/10 transition-colors`}
                                title={visibleKeys[key.key_id] ? "Hide key" : "Show key"}>
                                {visibleKeys[key.key_id] ? <EyeOff size={14} /> : <Eye size={14} />}
                              </button>
                              <button onClick={() => copyToClipboard(key.raw_key || key.key_preview)} data-testid={`copy-key-${key.key_id}`}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${textSecondary} hover:text-teal-400 hover:bg-teal-400/10 transition-colors`}
                                title="Copy API key">
                                <Copy size={14} /> Copy
                              </button>
                              <button onClick={() => handleRevokeKey(key.key_id)} disabled={revokingKey === key.key_id}
                                data-testid={`revoke-key-${key.key_id}`}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                                  confirmDelete === key.key_id
                                    ? 'text-white bg-red-500 hover:bg-red-600'
                                    : 'text-red-400 bg-red-400/10 hover:bg-red-400/20'
                                }`}>
                                {revokingKey === key.key_id ? <Loader2 size={12} className="animate-spin" /> : confirmDelete === key.key_id ? 'Confirm Delete' : 'Revoke'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Key className={`${textSecondary} mx-auto mb-2`} size={24} />
                        <p className={`text-sm ${textSecondary} mb-3`}>No API keys yet</p>
                        <button onClick={() => setShowCreateModal(true)} data-testid="create-first-api-key-button"
                          className="text-sm text-teal-400 hover:underline">Create your first key</button>
                      </div>
                    )}
                  </div>
                </section>

                {/* Webhook Info */}
                <div className="api-info-band" data-testid="api-authentication-note">
                  <div className="flex items-start gap-3">
                    <Info className="text-blue-400 mt-0.5 shrink-0" size={16} />
                    <p className={`text-xs ${textSecondary}`}>
                      Use API keys to trigger outbound calls from CRM workflows, Zapier, Make, or custom integrations.
                      Pass the key via <code className="px-1 py-0.5 rounded bg-blue-400/10 text-blue-400 text-[10px]">X-API-Key</code> header,{' '}
                      <code className="px-1 py-0.5 rounded bg-blue-400/10 text-blue-400 text-[10px]">?api_key=</code> query param, or{' '}
                      <code className="px-1 py-0.5 rounded bg-blue-400/10 text-blue-400 text-[10px]">api_key</code> in the request body.
                    </p>
                  </div>
                </div>

                {/* API Reference */}
                <section className="api-panel api-reference-panel" data-testid="api-reference-panel">
                  <div className="api-panel-heading">
                    <h2 data-testid="api-reference-heading">API Reference</h2>
                  </div>
                  
                  <div className="api-navigation-band">
                    <div className="api-reference-tabs">
                      {[
                        { id: 'outbound', icon: Phone, label: 'Outbound Call' },
                        { id: 'scraper', icon: Globe, label: 'Web Scraper' },
                        { id: 'screenshot', icon: Camera, label: 'Screenshot' },
                        { id: 'followup', icon: MessageCircle, label: 'Follow-Up Chat' },
                        { id: 'postcallsms', icon: MessageSquare, label: 'Post-Call SMS' }
                      ].map((tab) => (
                        <button key={tab.id} onClick={() => setActiveApiTab(tab.id)}
                          data-testid={`api-reference-tab-${tab.id}`} aria-pressed={activeApiTab === tab.id}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors">
                          <tab.icon size={14} /> {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="api-panel-body space-y-6">
                    {/* Description (1-line context) */}
                    <p className={`text-xs ${textSecondary} -mb-1`}>{activeDoc.description}</p>

                    <div className="api-endpoint-row">
                      <span className="api-endpoint-method" data-testid={`api-ref-method-${activeApiTab}`}>{activeDoc.method}</span>
                      <code className={`api-endpoint-url ${textPrimary}`} data-testid={`api-ref-url-${activeApiTab}`}>{`${apiBaseUrl}${activeDoc.endpoint}`}</code>
                      <button type="button" aria-label="Copy endpoint URL" onClick={() => copyToClipboard(`${apiBaseUrl}${activeDoc.endpoint}`)}
                        className="api-endpoint-copy" data-testid={`api-ref-url-copy-${activeApiTab}`}><Copy size={15} /></button>
                    </div>

                    {/* CUSTOM DATA */}
                    <div>
                      <h3 className="api-section-title" data-testid="api-custom-data-heading">Custom Data</h3>
                      <p className={`text-[11px] ${textSecondary} mb-2`}>
                        These are the key-value pairs you add in LeadConnector's "Custom Data" section of the Webhook action. The matching LeadConnector placeholders go in the Value column.
                      </p>
                      <div className={`rounded-xl border ${cardBorder} overflow-hidden`}>
                        <table className="w-full">
                          <thead>
                            <tr className={isDarkMode ? 'bg-white/5' : 'bg-gray-50'}>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Key</th>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Type</th>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Req</th>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeDoc.fields.map((f, i) => (
                              <tr key={i} className={`border-t ${cardBorder}`}>
                                <td className="px-3 py-2 font-mono text-xs text-teal-400 align-top">{f.name}</td>
                                <td className={`px-3 py-2 text-xs ${textSecondary} align-top`}>{f.type}</td>
                                <td className="px-3 py-2 align-top">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${f.required ? 'bg-red-400/10 text-red-400' : 'bg-gray-400/10 text-gray-400'}`}>
                                    {f.required ? 'yes' : 'no'}
                                  </span>
                                </td>
                                <td className={`px-3 py-2 text-xs ${textSecondary} align-top`}>{f.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* HEADERS */}
                    <section className="api-headers-section" data-testid={`api-headers-${activeApiTab}`}>
                      <h3 data-testid={`api-headers-heading-${activeApiTab}`}>Headers</h3>
                      <p className={`text-[11px] ${textSecondary} mb-2`}>
                        Add this in LeadConnector's "Headers" section of the Webhook action.
                      </p>
                      <div className={`rounded-xl border ${cardBorder} overflow-hidden`}>
                        <table className="w-full">
                          <thead>
                            <tr className={isDarkMode ? 'bg-white/5' : 'bg-gray-50'}>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Key</th>
                              <th className={`px-3 py-2 text-left text-[10px] font-medium ${textSecondary} uppercase`}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className={`border-t ${cardBorder}`}>
                              <td className="px-3 py-2 font-mono text-xs text-teal-400">Content-Type</td>
                              <td className={`px-3 py-2 font-mono text-xs ${textPrimary}`}>application/json</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </section>

                    {/* Post-Call SMS — Prompt & Templates editor (only shown when this tab is active) */}
                    {activeApiTab === 'postcallsms' && (
                      <div>
                        <PostCallPromptLibrary api={api} subAccountId={subAccountId} />
                        <details className={`text-[11px] ${textSecondary} mt-4`}>
                          <summary className="cursor-pointer hover:text-violet-300 transition-colors">
                            Reading the LeadConnector execution log
                          </summary>
                          <div className={`mt-2 p-3 rounded-lg ${inputBg} border ${cardBorder} space-y-1 font-mono`}>
                            <div className="opacity-90">Every response from this webhook has the same shape:</div>
                            <pre className="whitespace-pre">{`{
  "status": "success" | "failed" | "skipped",
  "reason": "<short human reason>",
  "data":   { call_id, outcome, sms_text, message_id, ... }
}`}</pre>
                            <div className="opacity-70 font-sans not-italic mt-2">
                              • <b>success</b> → Accepted by GHL, not verified delivery. Check <code>data.message_id</code> and the GHL conversation.<br/>
                              • <b>skipped</b> → <code>reason</code> tells you why (<code>llm_skipped</code>, <code>already_sent</code>, <code>no_phone</code>).<br/>
                              • <b>failed</b> → <code>reason</code> + <code>data.detail</code> explain the failure (expired LeadConnector OAuth, LeadConnector rejection, missing call, etc.).
                            </div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </section>

                {/* Webhook Setup Guide */}
                <section className="api-panel" data-testid="api-setup-guide-panel">
                  <div className="api-panel-heading">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-400/20 to-red-500/20 flex items-center justify-center">
                        <Link2 className="text-orange-400" size={18} />
                      </div>
                      <div>
                        <h2 data-testid="api-setup-guide-heading">
                          {SETUP_VIDEOS[activeApiTab] ? `Watch the LeadConnector Setup Video — ${activeDoc.title}` : 'Webhook Setup Guide'}
                        </h2>
                        <p className={`text-xs ${textSecondary}`}>
                          {SETUP_VIDEOS[activeApiTab]
                            ? 'Follow along — fill the webhook in LeadConnector while you watch'
                            : 'Connect AI agents to LeadConnector workflows'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {SETUP_VIDEOS[activeApiTab] ? (
                    <div className="p-4">
                      <div
                        className={`relative w-full overflow-hidden rounded-xl border ${cardBorder} bg-black`}
                        style={{ paddingTop: '56.25%' }}
                        data-testid={`webhook-setup-video-${activeApiTab}`}
                      >
                        <iframe
                          src={`https://fast.wistia.net/embed/iframe/${SETUP_VIDEOS[activeApiTab]}?seo=true&videoFoam=true`}
                          title={`${activeDoc.title} — LeadConnector setup tutorial`}
                          allow="autoplay; fullscreen"
                          allowFullScreen
                          frameBorder="0"
                          scrolling="no"
                          className="absolute inset-0 w-full h-full"
                        />
                      </div>
                    </div>
                  ) : (
                  <div className="p-4 space-y-3">
                    {[
                      { step: '1', title: 'Create an API Key', desc: 'Generate a key above. Copy the full key — you\'ll need it for authentication.' },
                      { step: '2', title: 'Set Up LeadConnector Workflow', desc: 'In LeadConnector: Automation → Workflows → Add HTTP/Webhook action.' },
                      { step: '3', title: 'Use the correct URL for your action', desc: null, urls: [
                        { label: 'Outbound Call', url: `${apiBaseUrl}/api/webhooks/outbound/trigger` },
                        { label: 'Web Scraper', url: `${apiBaseUrl}/api/webhooks/tools/scrape-website` },
                        { label: 'Screenshot', url: `${apiBaseUrl}/api/webhooks/tools/screenshot` },
                        { label: 'Follow-Up Chat', url: `${apiBaseUrl}/api/webhooks/follow-up-chat` },
                        { label: 'Post-Call SMS', url: `${apiBaseUrl}/api/webhooks/post-call-sms/trigger` },
                      ]},
                      { step: '4', title: 'Add Authentication', desc: 'Include api_key in the JSON body (recommended for LeadConnector) or pass via X-API-Key header.' },
                      { step: '5', title: 'Map Custom Fields', desc: 'For Web Scraper: Map business_name and business_description to LeadConnector custom fields in your workflow.' },
                    ].map((item) => (
                      <div key={item.step} className={`flex items-start gap-3 p-3 rounded-lg ${inputBg} border ${cardBorder}`}>
                        <span className="w-6 h-6 rounded-full bg-teal-400/10 text-teal-400 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{item.step}</span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${textPrimary}`}>{item.title}</p>
                          {item.urls ? (
                            <div className="mt-1.5 space-y-1.5">
                              {item.urls.map((u) => (
                                <div key={u.label} className="flex items-center gap-2">
                                  <span className={`text-[10px] ${textSecondary} w-24 shrink-0`}>{u.label}:</span>
                                  <code className="text-[11px] font-mono text-teal-400 truncate">{u.url}</code>
                                  <button onClick={() => copyToClipboard(u.url)} className="p-1 rounded text-teal-400 hover:bg-teal-400/10 shrink-0"><Copy size={11} /></button>
                                </div>
                              ))}
                            </div>
                          ) : item.desc ? (
                            <p className={`text-xs ${textSecondary}`}>{item.desc}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </section>
              </motion.div>
            )}

            {/* ═══════════════ BILLING ═══════════════ */}
            {activeCategory === 'billing' && (
              <motion.div key="billing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

                {/* ── Wallet ─────────────────────────────────────────────
                    Single unified card for Balance + Card on File + Auto-Pay.
                    Stripe stays in charge of payment processing — the
                    card metadata shown here is read-only display data
                    cached at setup time. */}
                <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`} data-testid="wallet-card">
                  <div className={`p-4 border-b ${cardBorder}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400/20 to-orange-500/20 flex items-center justify-center">
                          <DollarSign className="text-amber-400" size={18} />
                        </div>
                        <div>
                          <h2 className={`text-sm font-semibold ${textPrimary}`}>Wallet</h2>
                          <p className={`text-xs ${textSecondary}`}>Balance, payment method, and auto-pay</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                        billingData?.rebilling_enabled
                          ? 'bg-green-400/10 text-green-400'
                          : 'bg-teal-400/10 text-teal-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${billingData?.rebilling_enabled ? 'bg-green-400' : 'bg-teal-400'}`} />
                        {billingData?.rebilling_enabled ? 'Self-Managed' : 'Agency Managed'}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    {loadingWallet || loadingBilling ? (
                      <div className="flex justify-center py-8"><Loader2 className="animate-spin text-teal-400" size={24} /></div>
                    ) : (
                      <div className="space-y-5">
                        {/* Balance hero */}
                        <div className="flex items-end justify-between gap-4 flex-wrap">
                          <div>
                            <p className={`text-[10px] uppercase tracking-[0.18em] ${textSecondary} mb-1.5`}>Available Balance</p>
                            {wallet?.unlimited ? (
                              <p className="text-4xl font-bold text-amber-400 tracking-tight">Unlimited</p>
                            ) : (
                              <p className={`text-4xl font-bold ${textPrimary} tabular-nums tracking-tight`} data-testid="wallet-balance">
                                <span className="text-amber-400">$</span>{(wallet?.balance ?? billingData?.balance ?? 0).toFixed(2)}
                              </p>
                            )}
                          </div>
                          {billingData?.rebilling_enabled && (
                            <button
                              onClick={handleOpenAddBalance}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f] text-sm font-semibold shadow-lg shadow-amber-500/20 hover:brightness-110 transition-all"
                              data-testid="wallet-add-balance-btn"
                            >
                              <Plus size={15} />
                              Add Balance
                            </button>
                          )}
                        </div>

                        {/* Card on file row.
                            Self-managed (rebilling ON): sub-account has
                              its own card; added on first top-up via
                              Stripe Checkout. Shows "Visa •••• 4242"
                              once a card is on file; before that, a
                              soft slate hint that it will be added on
                              first top-up. No blocking warning — the
                              act of buying IS the act of adding the
                              card.
                            Agency-managed (rebilling OFF): no card UI
                              shown — the agency pays. */}
                        {billingData?.rebilling_enabled && (
                          <div
                            className={`flex items-center justify-between gap-3 p-3.5 rounded-xl ${inputBg} border ${cardBorder} flex-wrap`}
                            data-testid="wallet-card-on-file"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-10 h-10 rounded-lg ${
                                wallet?.cc_on_file ? 'bg-emerald-400/10 border border-emerald-400/30' : 'bg-white/[0.04] border border-white/10'
                              } flex items-center justify-center shrink-0`}>
                                <CreditCard size={16} className={wallet?.cc_on_file ? 'text-emerald-300' : textSecondary} />
                              </div>
                              <div className="min-w-0">
                                {wallet?.cc_on_file && wallet?.card?.last4 ? (
                                  <>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className={`text-sm font-medium ${textPrimary} capitalize`}>
                                        {wallet.card.brand || 'Card'} •••• {wallet.card.last4}
                                      </p>
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider bg-emerald-400/10 text-emerald-300 border border-emerald-400/20">
                                        Default
                                      </span>
                                    </div>
                                    <p className={`text-[11px] ${textSecondary}`}>
                                      {wallet.card.exp_month && wallet.card.exp_year
                                        ? `Exp ${String(wallet.card.exp_month).padStart(2, '0')}/${String(wallet.card.exp_year).slice(-2)}`
                                        : 'On file'}
                                    </p>
                                  </>
                                ) : wallet?.cc_on_file ? (
                                  <>
                                    <p className={`text-sm font-medium ${textPrimary}`}>Card on file</p>
                                    <p className={`text-[11px] ${textSecondary}`}>Default payment method</p>
                                  </>
                                ) : (
                                  <>
                                    <p className={`text-sm font-medium ${textPrimary}`}>No card on file yet</p>
                                    <p className={`text-[11px] ${textSecondary}`}>Your card will be added on your first top-up — Stripe handles the secure capture.</p>
                                  </>
                                )}
                              </div>
                            </div>
                            {wallet?.cc_on_file && (
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>Auto-Pay</span>
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                    wallet?.auto_topup?.enabled
                                      ? 'bg-teal-400/15 text-teal-300 border border-teal-400/30'
                                      : 'bg-white/[0.04] text-gray-400 border border-white/10'
                                  }`}
                                  data-testid="wallet-autopay-status"
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${wallet?.auto_topup?.enabled ? 'bg-teal-400' : 'bg-gray-500'}`} />
                                  {wallet?.auto_topup?.enabled ? 'On' : 'Off'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Auto-pay summary (only when on AND card present) */}
                        {wallet?.cc_on_file && wallet?.auto_topup?.enabled && billingData?.rebilling_enabled && (
                          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-teal-400/[0.06] border border-teal-400/20 text-[12px] text-teal-100/90">
                            <RefreshCw size={14} className="text-teal-400 mt-0.5 shrink-0" />
                            <p className="leading-relaxed">
                              When your balance drops below{' '}
                              <span className="text-white font-semibold">${Number(wallet.auto_topup.threshold_amount).toFixed(2)}</span>,
                              we&apos;ll automatically top it up with{' '}
                              <span className="text-teal-300 font-semibold">${Number(wallet.auto_topup.topup_amount).toFixed(2)}</span>
                              {' '}and charge your card on file.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Transaction History — Collapsible */}
                <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder}`}>
                  <button
                    onClick={() => { setTxOpen(!txOpen); if (!txOpen && transactions.length === 0) fetchTransactionHistory(); }}
                    className={`w-full p-4 flex items-center justify-between hover:opacity-80 transition-opacity`}
                    data-testid="settings-tx-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <Receipt className="text-teal-400" size={18} />
                      <div className="text-left">
                        <h2 className={`text-sm font-semibold ${textPrimary}`}>Transaction History</h2>
                        <p className={`text-[10px] ${textSecondary}`}>{txTotal > 0 ? `${txTotal} records` : 'View credit activity'}</p>
                      </div>
                    </div>
                    <ChevronDown size={16} className={`${textSecondary} transition-transform duration-200 ${txOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {txOpen && (
                    <>
                      {/* Date Filters */}
                      <div className={`px-4 pt-3 pb-2 flex flex-wrap items-end gap-2 border-t ${cardBorder}`}>
                        <div className="flex flex-col gap-0.5">
                          <label className={`text-[9px] uppercase tracking-wider ${textSecondary}`}>From</label>
                          <input type="date" value={txStartDate} onChange={e => setTxStartDate(e.target.value)}
                            className={`px-2.5 py-1.5 rounded-lg text-xs ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                            data-testid="settings-tx-start-date" />
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <label className={`text-[9px] uppercase tracking-wider ${textSecondary}`}>To</label>
                          <input type="date" value={txEndDate} onChange={e => setTxEndDate(e.target.value)}
                            className={`px-2.5 py-1.5 rounded-lg text-xs ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                            data-testid="settings-tx-end-date" />
                        </div>
                        <button onClick={handleTxApply}
                          className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-teal-400/10 text-teal-400 border border-teal-400/20 hover:bg-teal-400/20 transition-colors"
                          data-testid="settings-tx-apply">
                          Apply
                        </button>
                        {(txStartDate || txEndDate) && (
                          <button onClick={handleTxClear}
                            className={`px-3 py-1.5 rounded-lg text-[11px] ${inputBg} ${textSecondary} hover:opacity-80 transition-colors`}
                            data-testid="settings-tx-clear">
                            Clear
                          </button>
                        )}
                      </div>

                      <div className="p-4">
                        {/* Total spend banner — sub-account view, filtered totals */}
                        {!txLoading && transactions.length > 0 && (
                          <div
                            className={`flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-teal-400/[0.06] to-violet-400/[0.04] border border-teal-400/15`}
                            data-testid="settings-tx-usage-total-banner"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-teal-400/15 flex items-center justify-center">
                                <Receipt size={14} className="text-teal-400" />
                              </div>
                              <div>
                                <p className="text-[9px] uppercase tracking-wider text-teal-300/70 leading-tight">
                                  Total usage spend{txStartDate || txEndDate ? ' (filtered)' : ''}
                                </p>
                                <p className={`text-base font-semibold ${textPrimary} tabular-nums leading-tight`} data-testid="settings-tx-usage-total-amount">
                                  ${txUsageTotal.toFixed(txUsageTotal > 0 && txUsageTotal < 0.01 ? 4 : 2)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-[9px] uppercase tracking-wider ${textSecondary} leading-tight`}>Events</p>
                              <p className={`text-sm font-medium ${textPrimary} tabular-nums leading-tight`} data-testid="settings-tx-usage-total-count">
                                {txUsageCount.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        )}

                        {txLoading ? (
                          <div className="flex justify-center py-6"><Loader2 className="animate-spin text-teal-400" size={24} /></div>
                        ) : transactions.length > 0 ? (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full" data-testid="settings-tx-table">
                                <thead>
                                  <tr className={`border-b ${cardBorder}`}>
                                    <th className={`text-left text-[10px] font-medium pb-2 pr-3 ${textSecondary}`}>Date</th>
                                    <th className={`text-left text-[10px] font-medium pb-2 pr-3 ${textSecondary}`}>Type</th>
                                    <th className={`text-left text-[10px] font-medium pb-2 pr-3 ${textSecondary}`}>Description</th>
                                    <th className={`text-right text-[10px] font-medium pb-2 ${textSecondary}`}>Amount</th>
                                  </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-gray-100'}`}>
                                  {transactions.map((tx, idx) => (
                                    <tr key={idx} className={`${isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`} data-testid={`settings-tx-row-${idx}`}>
                                      <td className={`py-2.5 pr-3 text-xs ${textSecondary} whitespace-nowrap`}>
                                        {tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '---'}
                                      </td>
                                      <td className="py-2.5 pr-3">{getTypeBadge(tx.type)}</td>
                                      <td className={`py-2.5 pr-3 text-xs ${textPrimary} max-w-[200px] truncate`}>{tx.description || '---'}</td>
                                      <td className="py-2.5 text-xs font-semibold text-right whitespace-nowrap" data-testid={`settings-tx-amount-${idx}`}>
                                        {(() => {
                                          const a = Number(tx.amount || 0);
                                          const wb = typeof tx.would_be_amount === 'number' ? tx.would_be_amount : a;
                                          const isUnlimited = tx.is_unlimited || (a === 0 && wb < 0);
                                          const fmt = (v) => {
                                            const abs = Math.abs(v);
                                            if (abs === 0) return '$0.00';
                                            const d = abs > 0 && abs < 0.01 ? 4 : 2;
                                            return `$${abs.toFixed(d)}`;
                                          };
                                          if (isUnlimited) {
                                            return (
                                              <span className="inline-flex items-center gap-1">
                                                <span className="text-teal-400/80 tabular-nums">~{fmt(wb)}</span>
                                                <span className="text-[8px] uppercase tracking-wider font-bold text-teal-400/50 px-1 rounded border border-teal-400/20">unl</span>
                                              </span>
                                            );
                                          }
                                          if (a > 0) return <span className="text-emerald-400 tabular-nums">+{fmt(a)}</span>;
                                          if (a < 0) return <span className="text-rose-400 tabular-nums">-{fmt(a)}</span>;
                                          return <span className="text-gray-500 tabular-nums">$0.00</span>;
                                        })()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {txTotalPages > 1 && (
                              <div className={`flex items-center justify-between mt-3 pt-3 border-t ${cardBorder}`}>
                                <p className={`text-[10px] ${textSecondary}`}>
                                  {txPage * TX_PAGE_SIZE + 1}–{Math.min((txPage + 1) * TX_PAGE_SIZE, txTotal)} of {txTotal}
                                </p>
                                <div className="flex gap-1.5">
                                  <button onClick={() => handleTxPageChange(txPage - 1)} disabled={txPage === 0}
                                    className={`px-2.5 py-1 rounded text-[10px] ${inputBg} ${textSecondary} disabled:opacity-30 hover:opacity-80 transition-colors`}>
                                    Prev
                                  </button>
                                  <button onClick={() => handleTxPageChange(txPage + 1)} disabled={txPage >= txTotalPages - 1}
                                    className={`px-2.5 py-1 rounded text-[10px] ${inputBg} ${textSecondary} disabled:opacity-30 hover:opacity-80 transition-colors`}>
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className={`text-sm text-center py-6 ${textSecondary}`}>
                            {txStartDate || txEndDate ? 'No transactions in this date range' : 'No transactions yet'}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Add Balance Modal ─────────────────────────────────
                    Same look + feel as the agency workspace top-up
                    modal so users get a consistent experience whether
                    they're paying at the agency or sub-account level.
                    Stripe still handles the actual charge via Checkout. */}
                <AnimatePresence>
                  {showAddBalanceModal && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
                      onClick={() => !purchasing && setShowAddBalanceModal(false)}
                      data-testid="add-balance-modal"
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-2xl rounded-2xl overflow-hidden relative my-auto"
                        onClick={e => e.stopPropagation()}
                        style={{
                          background: 'radial-gradient(120% 120% at 0% 0%, #1a1d24 0%, #0d0f13 55%, #080a0e 100%)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        {/* Ambient corner washes */}
                        <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-25 blur-3xl"
                             style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
                        <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full opacity-15 blur-3xl"
                             style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 70%)' }} />

                        <div className="relative px-6 pt-6 pb-4 border-b border-white/[0.05]">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                                   style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
                                <Zap size={20} className="text-[#0a0b0f]" fill="#0a0b0f" />
                              </div>
                              <div>
                                <h3 className="text-xl font-semibold text-white tracking-tight">Add Balance</h3>
                                <p className="text-xs text-slate-400">Pay-as-you-go · Balance never expires</p>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowAddBalanceModal(false)}
                              disabled={purchasing}
                              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-50"
                              data-testid="add-balance-modal-close"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="relative p-6 space-y-6">
                          {/* Bundle grid */}
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Choose an amount</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                              {TOP_UP_BUNDLES.map(({ amt, desc, popular }) => {
                                const isProcessing = purchasing && topUpAmount === amt;
                                return (
                                  <button
                                    key={amt}
                                    onClick={() => handleTopUpDollar(amt)}
                                    disabled={purchasing}
                                    className={`group relative rounded-xl p-3.5 text-left transition-all overflow-hidden border ${
                                      popular
                                        ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/[0.08] to-amber-500/[0.03] shadow-[0_0_30px_-8px_rgba(251,191,36,0.35)] hover:shadow-[0_0_40px_-6px_rgba(251,191,36,0.5)]'
                                        : 'border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] hover:border-teal-400/35 hover:from-teal-400/[0.06]'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    data-testid={`add-balance-bundle-${amt}`}
                                  >
                                    {popular && (
                                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[8px] font-extrabold tracking-wider bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f]">
                                        POPULAR
                                      </div>
                                    )}
                                    <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{desc}</p>
                                    <div className="flex items-baseline gap-1 mb-2">
                                      <span className={`text-2xl font-bold ${popular ? 'text-amber-300' : 'text-white'}`}>${amt}</span>
                                    </div>
                                    <div className={`inline-flex items-center justify-center w-full gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-semibold transition-all ${
                                      popular
                                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f] group-hover:brightness-110'
                                        : 'bg-white text-[#0a0b0f] group-hover:bg-slate-100'
                                    }`}>
                                      {isProcessing ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={10} fill="currentColor" />}
                                      {isProcessing ? 'Opening…' : 'Buy Now'}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Custom amount */}
                          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Custom amount</p>
                              <span className="text-[10px] text-slate-500">Min $10</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <div className="flex-1 flex items-center bg-[#0d0e12] border border-white/[0.08] rounded-lg overflow-hidden focus-within:border-teal-400/40">
                                <span className="px-3 text-slate-500 text-sm">$</span>
                                <input
                                  type="number"
                                  min="10"
                                  max="9999"
                                  step="1"
                                  placeholder="Enter amount"
                                  value={topUpCustom}
                                  onChange={e => setTopUpCustom(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      handleTopUpDollar(parseFloat(topUpCustom));
                                    }
                                  }}
                                  className="flex-1 bg-transparent py-2.5 pr-3 text-white text-sm outline-none placeholder-slate-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  data-testid="add-balance-custom-input"
                                />
                              </div>
                              <button
                                onClick={() => handleTopUpDollar(parseFloat(topUpCustom))}
                                disabled={purchasing || !topUpCustom || parseFloat(topUpCustom) < 10}
                                className="px-4 py-2.5 rounded-lg bg-white text-[#0a0b0f] text-sm font-semibold hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="add-balance-custom-btn"
                              >
                                Buy {topUpCustom && parseFloat(topUpCustom) >= 10 ? `$${parseFloat(topUpCustom).toFixed(0)}` : ''}
                              </button>
                            </div>
                          </div>

                          {/* Auto-Pay inline section. No "must add card"
                              warning — the Stripe Checkout flow captures
                              the card on the first top-up. Users can
                              save their auto-pay preferences before a
                              card is on file; the preferences activate
                              the moment the card is saved (next top-up). */}
                          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3" data-testid="add-balance-autopay-section">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-lg bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                                  <RefreshCw className="text-teal-400" size={15} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-white">Auto-Pay</p>
                                  <p className="text-[11px] text-slate-500">Refill automatically when balance runs low</p>
                                </div>
                              </div>
                              <button
                                onClick={() => setAutoPayForm(p => ({ ...p, enabled: !p.enabled }))}
                                className={`w-11 h-6 rounded-full transition-colors ${
                                  autoPayForm.enabled ? 'bg-teal-400' : 'bg-white/10'
                                }`}
                                data-testid="add-balance-autopay-toggle"
                              >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${autoPayForm.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                            </div>

                            {autoPayForm.enabled && (
                              <>
                                <div className="grid grid-cols-2 gap-2.5">
                                  <div>
                                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Top up when balance falls below</label>
                                    <div className="mt-1 flex items-center bg-[#0d0e12] border border-white/[0.08] rounded-lg overflow-hidden focus-within:border-teal-400/40">
                                      <span className="px-2.5 text-slate-500 text-xs">$</span>
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={autoPayForm.threshold_amount}
                                        onChange={e => setAutoPayForm(p => ({ ...p, threshold_amount: Math.max(0, Number(e.target.value) || 0) }))}
                                        className="flex-1 bg-transparent py-2 pr-2.5 text-white text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        data-testid="autopay-threshold-input"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Balance to add when triggered</label>
                                    <div className="mt-1 flex items-center bg-[#0d0e12] border border-white/[0.08] rounded-lg overflow-hidden focus-within:border-teal-400/40">
                                      <span className="px-2.5 text-slate-500 text-xs">$</span>
                                      <input
                                        type="number"
                                        min="10"
                                        max="9999"
                                        step="1"
                                        value={autoPayForm.topup_amount}
                                        onChange={e => setAutoPayForm(p => ({ ...p, topup_amount: Math.max(0, Math.min(9999, Number(e.target.value) || 0)) }))}
                                        className="flex-1 bg-transparent py-2 pr-2.5 text-white text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        data-testid="autopay-amount-input"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* Live description */}
                                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-teal-400/[0.07] border border-teal-400/20">
                                  <CheckCircle size={14} className="text-teal-400 mt-0.5 shrink-0" />
                                  <p className="text-[12px] text-teal-100/90 leading-relaxed">
                                    When your balance drops below{' '}
                                    <span className="text-white font-semibold">${Number(autoPayForm.threshold_amount || 0).toFixed(2)}</span>,
                                    we&apos;ll automatically top it up with{' '}
                                    <span className="text-teal-300 font-semibold">${Number(autoPayForm.topup_amount || 0).toFixed(2)}</span>
                                    {' '}and charge your card on file.
                                  </p>
                                </div>
                              </>
                            )}

                            <button
                              onClick={handleSaveAutoPay}
                              disabled={savingAutoPay}
                              className="w-full px-3 py-2 rounded-lg text-xs font-semibold bg-teal-400/10 text-teal-300 border border-teal-400/30 hover:bg-teal-400/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="add-balance-save-autopay-btn"
                            >
                              {savingAutoPay ? 'Saving…' : 'Save Auto-Pay Settings'}
                            </button>
                          </div>

                          <p className="text-[11px] text-slate-500 text-center">
                            Secure payment via Stripe · Balance never expires
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* ═══════════════ BYOK ═══════════════ */}
            {activeCategory === 'byok' && (
              <motion.div key="byok" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder}`}>
                  <div className={`p-4 border-b ${cardBorder}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400/20 to-purple-500/20 flex items-center justify-center">
                        <Key className="text-violet-400" size={18} />
                      </div>
                      <div>
                        <h2 className={`text-sm font-semibold ${textPrimary}`}>Bring Your Own Key (BYOK)</h2>
                        <p className={`text-xs ${textSecondary}`}>Connect your own AI API keys to avoid credit charges for chat</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    {loadingBilling ? (
                      <div className="flex justify-center py-6"><Loader2 className="animate-spin text-teal-400" size={24} /></div>
                    ) : billingData?.rebilling_enabled ? (
                      <div className="space-y-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-violet-400/5' : 'bg-violet-50'} border border-violet-400/20`}>
                          <p className={`text-xs ${textSecondary}`}>
                            When you connect your own API keys, chat messages processed through that provider won't be charged credits.
                            Voice calls and KB queries are still credit-based.
                          </p>
                        </div>
                        
                        {/* OpenAI Key */}
                        <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#10A37F20' }}>
                              <Key size={14} style={{ color: '#10A37F' }} />
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>OpenAI</p>
                              <p className={`text-xs ${textSecondary}`}>GPT Models</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              placeholder="sk-..."
                              defaultValue={billingData?.byok_keys?.openai || ''}
                              data-testid="byok-openai-key"
                              className={`flex-1 px-3 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50 text-sm`}
                              onBlur={async (e) => {
                                if (e.target.value) {
                                  try {
                                    await api.put(`/api/sub-accounts/${subAccountId}/byok`, { provider: 'openai', api_key: e.target.value });
                                    toast.success('OpenAI key saved');
                                  } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save key'); }
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* Claude Key */}
                        <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D9775720' }}>
                              <Key size={14} style={{ color: '#D97757' }} />
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>Claude (Anthropic)</p>
                              <p className={`text-xs ${textSecondary}`}>Claude AI Models</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              placeholder="sk-ant-..."
                              defaultValue={billingData?.byok_keys?.anthropic || ''}
                              data-testid="byok-claude-key"
                              className={`flex-1 px-3 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50 text-sm`}
                              onBlur={async (e) => {
                                if (e.target.value) {
                                  try {
                                    await api.put(`/api/sub-accounts/${subAccountId}/byok`, { provider: 'anthropic', api_key: e.target.value });
                                    toast.success('Claude key saved');
                                  } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save key'); }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Key className={`${textSecondary} mx-auto mb-2`} size={28} />
                        <p className={`text-sm font-medium ${textPrimary} mb-1`}>BYOK Not Available</p>
                        <p className={`text-xs ${textSecondary}`}>
                          {!billingData ? 'Loading billing information...' :
                           !billingData?.rebilling_enabled
                              ? 'BYOK is available when re-billing is enabled. Contact your agency to enable self-service billing.'
                              : 'Unable to determine BYOK eligibility.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowCreateModal(false); setNewlyCreatedKey(null); setNewKeyName(''); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-md ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl border ${cardBorder} shadow-2xl`}
              onClick={e => e.stopPropagation()}>
              <div className={`p-5 border-b ${cardBorder} flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Key className="text-white" size={18} />
                  </div>
                  <h3 className={`text-base font-semibold ${textPrimary}`}>{newlyCreatedKey ? 'Key Created' : 'Create API Key'}</h3>
                </div>
                <button onClick={() => { setShowCreateModal(false); setNewlyCreatedKey(null); setNewKeyName(''); }}
                  className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/10`}><X size={18} /></button>
              </div>

              <div className="p-5">
                {newlyCreatedKey ? (
                  <div className="space-y-3">
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-green-400/5' : 'bg-green-50'} border border-green-400/20`}>
                      <div className="flex items-start gap-2 mb-2">
                        <CheckCircle className="text-green-400 mt-0.5" size={16} />
                        <div>
                          <p className={`text-sm font-medium ${textPrimary}`}>Ready!</p>
                          <p className={`text-[10px] ${textSecondary}`}>Copy now — won't be shown again.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className={`flex-1 px-2 py-2 rounded-lg ${isDarkMode ? 'bg-black/40' : 'bg-gray-900'} text-green-400 text-xs font-mono break-all`}>
                          {newlyCreatedKey.api_key}
                        </code>
                        <button onClick={() => copyToClipboard(newlyCreatedKey.api_key)}
                          className="p-2 rounded-lg bg-green-400/10 text-green-400 hover:bg-green-400/20"><Copy size={16} /></button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className={`block text-xs ${textSecondary} mb-1.5`}>Key Name</label>
                    <input type="text" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., LeadConnector Production" autoFocus
                      className={`w-full px-3 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50 text-sm`} />
                  </div>
                )}
              </div>

              <div className={`p-5 border-t ${cardBorder}`}>
                {newlyCreatedKey ? (
                  <button onClick={() => { setShowCreateModal(false); setNewlyCreatedKey(null); }}
                    className="w-full py-2.5 rounded-xl bg-teal-400 text-[#08090d] font-medium text-sm hover:bg-teal-300">Done</button>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => { setShowCreateModal(false); setNewKeyName(''); }}
                      className={`flex-1 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10`}>Cancel</button>
                    <button onClick={handleCreateKey} disabled={creatingKey || !newKeyName.trim()}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-teal-400 text-[#08090d] font-medium text-sm hover:bg-teal-300 disabled:opacity-50">
                      {creatingKey ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Key size={14} /> Create</>}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SubAccountSettingsPage = () => (
  <SubAccountLayout>
    <SubAccountSettingsContent />
  </SubAccountLayout>
);

export default SubAccountSettingsPage;
